import { useTranslation } from 'react-i18next';
import { useInfluencerPermissions, useUserRoles } from '@anytag/auth';
import { SocialAccountStatus, SocialAccountType } from '@anytag/graphql';
import { ColumnDef } from '@tanstack/react-table';
import { TableCell } from '@anytag/shared-atoms';
import { useGenerateInfluencerProfileLink } from '@anytag/shared-hooks';
import {
  PostsSection,
  InfluencerAvatar,
  InfluencerPostDetailsModal,
  InfluencerWidgetInformation
} from '@anytag/shared-molecules';
import { formatIntNumber, formatPercent } from '@anytag/shared-utils';
import { TrendingInfluencersTabType } from '../../TrendingInfluencersFilter/schemaTypes';
import { TrendingInfluencerProfileType } from '../types';

interface Props {
  tab: TrendingInfluencersTabType;
}

export const useTrendingInfluencersTable = ({ tab }: Props) => {
  const { t } = useTranslation();
  const { isAdvertiser, isTalentAgency } = useUserRoles();
  const { hideEditInfluencerBtn } = useInfluencerPermissions();
  const { generateProfileLink } = useGenerateInfluencerProfileLink({
    disabled: isAdvertiser || isTalentAgency || hideEditInfluencerBtn
  });

  const isYoutube = tab === TrendingInfluencersTabType.YOUTUBE;
  const columns: ColumnDef<TrendingInfluencerProfileType>[] = [
    {
      accessorKey: 'rank',
      meta: { align: 'center' },
      header: t<string>('HeaderColumn.Ranking'),
      cell: ({ row }) => <TableCell.Text value={String(row.original.rank)} className="min-w-15 pr-2 text-center" />
    },
    {
      accessorKey: 'name',
      header: t<string>('HeaderColumn.Influencer'),
      cell: ({ row }) => {
        const { id, age, name, gender, status, avatar, country, socialMedia, socialAccountId } = row.original;
        const socialAccountType = socialMedia || (tab as unknown as SocialAccountType);

        return (
          <div className="flex max-w-75 items-center py-4">
            <InfluencerAvatar size="80px" src={avatar || ''} socialAccountType={socialAccountType} />
            <InfluencerWidgetInformation
              age={age}
              name={name}
              gender={gender}
              country={country}
              isSignedUp={status === SocialAccountStatus.SIGNED_UP}
              profileUrl={generateProfileLink(
                id,
                socialAccountId ? { filter: { id: socialAccountId, sm: socialAccountType } } : undefined
              )}
            />
          </div>
        );
      }
    },
    {
      accessorKey: 'followers',
      header: t<string>('HeaderColumn.Follower'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.socialAccountFollowers)} />
    },
    {
      accessorKey: 'averageViews',
      header: t<string>('HeaderColumn.Average Views'),
      meta: { align: 'right', hidden: !isYoutube },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.socialAccountAverageViews)} />
    },
    {
      accessorKey: 'engagementRate',
      header: t<string>('HeaderColumn.ER'),
      meta: { align: 'right', hidden: isYoutube },
      cell: ({ row }) => (
        <TableCell.Number value={formatPercent(row.original.socialAccountEngagementRate, false)} unit="%" />
      )
    },
    {
      accessorKey: 'followersGrowth',
      header: t<string>('HeaderColumn.Follower Growth'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.followersGrowthRate, false)} unit="%" />
    },
    {
      accessorKey: 'posts',
      header: t<string>('HeaderColumn.Posts'),
      meta: { align: 'left' },
      cell: ({ row }) => {
        const { id, popularPosts } = row.original;
        const influencerId = Number(id);
        const posts = popularPosts.map(({ id, thumbNail, type }) => ({ id, type, thumbNail, influencerId }));

        return (
          <>
            <PostsSection influencerId={influencerId} posts={posts} />
            <InfluencerPostDetailsModal posts={posts} />
          </>
        );
      }
    }
  ];

  return { columns };
};
